import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { snakeCase } from 'lodash';

import { Configuration, GetPreTradePositionRequest, PositionApi } from '../_openapi/portfolio-optimizer';
import { processFilter } from '../widgets/filters/filterUtils';
import { parseBrokerAccountId } from './broker-account-id';
import { defaultApiConfig } from './defaultApiConfig';

const positionApi = new PositionApi(new Configuration(defaultApiConfig));

export const usePositions = (
    {
        dataVariant = 'report',
        pagination,
        sorting,
        columnFilters,
    }: {
        dataVariant?: DataVariant;
        pagination?: PaginationState;
        sorting?: SortingState;
        columnFilters?: ColumnFiltersState;
    },
    options?: { throwOnError?: boolean },
) =>
    useQuery({
        ...options,
        queryKey: ['position', 'list', { pagination, sorting, columnFilters, dataVariant }],
        queryFn: () => {
            const query: GetPreTradePositionRequest = {};

            if (pagination) {
                query.pageIndex = pagination.pageIndex;
                query.pageSize = pagination.pageSize;
            }

            if (sorting?.length) {
                query.sortLabel = snakeCase(sorting[0].id);
                query.sortDesc = sorting[0].desc;
            }

            if (columnFilters?.length) {
                query.filter = columnFilters.map(processFilter);
            }

            switch (dataVariant) {
                case 'report':
                    return positionApi.getReportPosition(query);
                case 'preTrade':
                    return positionApi.getPreTradePosition(query);
                default:
                    throw new Error('Unknown data variant');
            }
        },
        placeholderData: keepPreviousData,
    });

export const usePositionDataAvailability = () =>
    useQuery({
        queryKey: ['position', 'dataAvailability'],
        queryFn: async () => {
            const query: GetPreTradePositionRequest = { pageIndex: 0, pageSize: 1 };
            const report = await positionApi.getReportPosition(query);
            const preTrade = await positionApi.getPreTradePosition(query);

            return {
                report: report.asOfDate,
                preTrade: preTrade.asOfDate,
            };
        },
    });

export const useUniverseExcludedPositions = (brokerAccountIds: string[]) => {
    const ids = [...new Set(brokerAccountIds)].sort();
    return useQuery({
        queryKey: ['position', 'universeExcluded', ids],
        queryFn: ({ signal }) => positionApi.getUniverseExcludedPositions({ requestBody: ids }, { signal }),
        enabled: ids.length > 0,
        throwOnError: false,
    });
};

export const useBrokerAccountPositions = (brokerAccountIds: string[], options?: { throwOnError?: boolean }) => {
    const ids = [...new Set(brokerAccountIds)].sort();
    return useQuery({
        ...options,
        queryKey: ['position', 'brokerAccounts', ids],
        queryFn: async ({ signal }) => {
            const pages = await Promise.all(
                ids.map((id) => {
                    const { brokerId, accountNumber } = parseBrokerAccountId(id);
                    // The backend returns all positions when pageIndex/pageSize are omitted.
                    return positionApi.getReportPosition(
                        {
                            filter: [
                                processFilter({ id: 'brokerId', value: brokerId }),
                                processFilter({ id: 'a_c', value: accountNumber }),
                            ],
                        },
                        { signal },
                    );
                }),
            );
            return pages.flatMap((page) => page.positions);
        },
        enabled: ids.length > 0,
        throwOnError: false,
    });
};

export const useInferredPositions = (accountGUIDs: string[]) => {
    const sortedGUIDs = [...accountGUIDs].sort();
    return useQuery({
        queryKey: ['position', 'inferred', sortedGUIDs],
        queryFn: () => positionApi.getInferredPositions({ requestBody: sortedGUIDs }),
        enabled: sortedGUIDs.length > 0,
    });
};
